/*
  This file is being used in the 'frontends/app/host' where it is still having issues resolving components from src/app.
  Do not add code that imports components into this file until that is resolved.
*/

import {
  FORGE_INFERENCE_GETTING_STARTED_PATH,
  FORGE_INFERENCE_PATH,
  FORGE_INFERENCE_PATHS,
} from '../inference/paths';
import {
  ACCOUNT_SETTINGS_PATHS,
  ACCOUNT_SETTINGS_USAGE_PAGE_PATHS,
  PROJECT_SETTINGS_PATH,
  USER_SETTINGS_PATH,
} from './account/accountSettingPaths';
import {
  ADMIN_AUDIT_LOGS_PATH,
  ADMIN_FULLY_CONNECTED_PATH,
  ADMIN_GLUE_PATH,
  ADMIN_HOME_PATH,
  ADMIN_ORGANIZATION_PATHS,
  ADMIN_PRICING_PLANS_PATH,
  ADMIN_RAMP_PATH,
  ADMIN_USERS_PATH,
} from './admin/adminPaths';
import {ARIA_PATHS} from './aria/ariaPaths';
import {ARTIFACT_VERSION_PATH} from './artifacts/artifactPaths';
import {
  AUTHORIZE_PATH,
  CHANGE_PASSWORD_PATH,
  CLI_LOGIN_CANCELED_PATH,
  CLI_LOGIN_PATH,
  CLI_LOGIN_SUCCESS_PATH,
  LOGIN_PATH,
  LOGIN_REDIRECT_PATH,
  LOGOUT_PATH,
} from './auth/authPaths';
import {AUTOMATIONS_PATH} from './automations/automationsPaths';
import {
  INFERENCE_COLLECTION_PATH,
  INFERENCE_COMPARE_PATH,
  INFERENCE_MODEL_PATH,
} from './inference/inferencePaths';
import {
  AGENT_PATHS,
  ENTITY_LAUNCH_PATHS,
  LAUNCH_PATH,
  PROJECT_LAUNCH_TAB_PATH,
  QUEUE_PATHS,
} from './launch/launchPaths';
import {
  FORGE_NOTEBOOK_PATH,
  FORGE_NOTEBOOK_VIEW_PATH,
  MARIMO_NOTEBOOK_EDIT_PATH,
  MARIMO_NOTEBOOK_VIEW_PATH,
  NOTEBOOKS_HOME_PATHS,
} from './notebooks/notebookPaths';
import {
  ACADEMIC_APPLICATION_PATH,
  ONBOARDING_FLOW_PATH,
  QUICKSTART_PATH,
  STARTUP_PROGRAM_PATH,
  TRIAL_END_PATH,
} from './onboarding/onboardingPaths';
import {
  ORG_ALERTS,
  ORG_API_KEYS,
  ORG_BILLING,
  ORG_DASHBOARD,
  ORG_SERVICE_ACCOUNTS,
  ORG_SETTINGS,
  ORG_SETTINGS_PATHS,
  ORG_TEAMS,
  ORG_USER_ACTIVITY,
} from './organization/orgPaths';
import {HOME_PATH, NO_ACCESS_PATH, PROJECT_PATHS, REDIRECT_PATH} from './paths';
import {REGISTRY_PATHS} from './registry/registryPaths';
import {
  GALLERY_PATH,
  GALLERY_PATH_SEGMENT,
  INACTIVE_GALLERY_SEGMENT_PATH,
  OLD_GALLERY_DISCUSSION_EDIT_PATH,
  OLD_GALLERY_DISCUSSION_VIEW_PATH,
  OLD_REPORT_PAGE_EDIT_PATH,
  OLD_REPORT_PAGE_VIEW_PATH,
  redirectOffInactiveGallerySegment,
  REPORT_PAGE_EDIT_PATH,
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
} from './report/reportPaths';
import {GLOBAL_SANDBOXES_PATHS} from './sandboxes/sandboxesPaths';
import {
  PROFILE_BASE_PATH,
  PROFILE_BASE_PROJECTS_PATH,
  PROFILE_BASE_REPORTS_PATH,
  PROFILE_BASE_SETTINGS_PATH,
  PROFILE_BASE_STARS_PATH,
  PROFILE_NOTEBOOKS_PATH,
  PROFILE_PATH,
  PROFILE_PROJECTS_PATH,
  PROFILE_REPORTS_PATH,
  PROFILE_STARS_PATH,
  TEAM_MEMBERS_PATH,
  TEAM_SERVICE_ACCOUNTS_PATH,
  TEAM_SETTINGS_PATH,
} from './team/teamPaths';
import type {RedirectTarget} from './types';
import {
  GLOBAL_HISTORIC_USAGE_PATH,
  GLOBAL_HISTORIC_USAGE_REDIRECT_PATH,
  GLOBAL_HISTORIC_USAGE_REDIRECT_TO_PATH,
  HISTORIC_USAGE_PATH,
  HISTORIC_USAGE_REDIRECT_PATH,
  HISTORIC_USAGE_REDIRECT_TO_PATH,
  STORAGE_EXPLORER_TRACKED_PATH,
  USAGE_PATH,
} from './usage/usagePaths';
import {GROUP_PATHS} from './workspace/groupWorkspacePaths';
import {PROJECT_BASE_PATH} from './workspace/multiRunWorkspacePaths';
import {CREATE_SWEEP_PATH, SWEEP_PATHS} from './workspace/sweepWorkspacePaths';

export type SimpleRoute = {
  path: string | string[];
  exact?: true;
  redirect?: RedirectTarget;
};

export const ROUTES: SimpleRoute[] = [
  {
    path: '/dev/error',
    exact: true,
  },
  {
    path: '/',
    exact: true,
  },
  {
    path: HOME_PATH,
    exact: true,
  },
  {
    path: LOGIN_PATH,
  },
  {
    path: LOGIN_REDIRECT_PATH,
  },
  {
    path: LOGOUT_PATH,
  },
  {
    path: CHANGE_PASSWORD_PATH,
  },
  {
    path: AUTHORIZE_PATH,
  },
  {
    path: CLI_LOGIN_CANCELED_PATH,
    exact: true,
  },
  {
    path: CLI_LOGIN_PATH,
    exact: true,
  },
  {
    path: CLI_LOGIN_SUCCESS_PATH,
    exact: true,
  },
  {
    path: QUICKSTART_PATH,
    exact: true,
  },
  {
    path: ONBOARDING_FLOW_PATH,
  },
  {
    path: STARTUP_PROGRAM_PATH,
    exact: true,
  },
  {
    path: ADMIN_AUDIT_LOGS_PATH,
    exact: true,
  },
  {
    path: ADMIN_ORGANIZATION_PATHS,
  },
  {
    path: ADMIN_PRICING_PLANS_PATH,
  },
  {
    path: ADMIN_USERS_PATH,
  },
  {
    path: ADMIN_FULLY_CONNECTED_PATH,
  },
  {
    path: ADMIN_RAMP_PATH,
  },
  {
    path: ADMIN_GLUE_PATH,
  },
  {
    path: ADMIN_HOME_PATH,
  },
  {
    path: GALLERY_PATH,
  },
  {
    path: INACTIVE_GALLERY_SEGMENT_PATH,
    redirect: redirectOffInactiveGallerySegment,
  },
  {
    path: '/fullyconnected',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: '/fc',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: '/gallery',
    redirect: `/${GALLERY_PATH_SEGMENT}`,
  },
  {
    path: AUTOMATIONS_PATH,
    exact: true,
  },
  {
    path: NOTEBOOKS_HOME_PATHS,
    exact: true,
  },
  {
    path: FORGE_NOTEBOOK_PATH,
    exact: true,
  },
  {
    path: FORGE_NOTEBOOK_VIEW_PATH,
    exact: true,
  },
  {
    path: FORGE_INFERENCE_PATHS,
    exact: true,
  },
  {
    path: FORGE_INFERENCE_PATH,
    exact: true,
    redirect: FORGE_INFERENCE_GETTING_STARTED_PATH,
  },
  {
    path: [
      INFERENCE_MODEL_PATH,
      INFERENCE_COLLECTION_PATH,
      INFERENCE_COMPARE_PATH,
    ],
  },
  {
    path: '/weave',
    exact: true,
  },
  {
    path: '/__test/simple_error',
    exact: true,
  },
  {
    path: USER_SETTINGS_PATH,
    exact: true,
  },
  {
    path: ACCOUNT_SETTINGS_PATHS,
    exact: true,
  },
  {
    path: ACCOUNT_SETTINGS_USAGE_PAGE_PATHS,
    exact: true,
  },
  {
    path: PROJECT_SETTINGS_PATH,
    exact: true,
  },
  {
    path: '/create-team',
    exact: true,
  },
  {
    path: [
      ORG_DASHBOARD,
      ORG_TEAMS,
      ORG_SERVICE_ACCOUNTS,
      ORG_API_KEYS,
      ORG_USER_ACTIVITY,
      ORG_BILLING,
      ORG_ALERTS,
    ],
    exact: true,
  },
  {
    path: '/org/roles',
    redirect: ORG_SETTINGS,
  },
  {
    path: ORG_SETTINGS_PATHS,
    exact: true,
  },
  {
    path: [
      USAGE_PATH,
      STORAGE_EXPLORER_TRACKED_PATH,
      '/usage/:entityName/:filePath*',
    ],
    exact: true,
  },
  {
    path: '/storage/:entityName/tracked/:filePath*',
    redirect: STORAGE_EXPLORER_TRACKED_PATH,
  },
  {
    path: '/storage/:entityName/:filePath*',
    redirect: '/usage/:entityName/:filePath*',
  },
  {
    path: '/storage/:entityName',
    redirect: '/usage/:entityName',
  },
  // New profile-specific routes (canonical URLs for personal entities)
  {
    path: PROFILE_BASE_PATH,
    exact: true,
  },
  {
    path: PROFILE_BASE_REPORTS_PATH,
    exact: true,
  },
  {
    path: PROFILE_BASE_PROJECTS_PATH,
    exact: true,
  },
  {
    path: PROFILE_BASE_STARS_PATH,
    exact: true,
  },
  {
    path: PROFILE_BASE_SETTINGS_PATH,
    exact: true,
  },
  // Legacy profile paths (redirect to /profile/:entityName for personal entities)
  {
    path: PROFILE_REPORTS_PATH,
    exact: true,
  },
  {
    path: PROFILE_PROJECTS_PATH,
    exact: true,
  },
  {
    path: PROFILE_NOTEBOOKS_PATH,
    exact: true,
  },
  {
    path: PROFILE_STARS_PATH,
    exact: true,
  },
  {
    path: TEAM_MEMBERS_PATH,
    exact: true,
  },
  {
    path: TEAM_SERVICE_ACCOUNTS_PATH,
    exact: true,
  },
  {
    path: TEAM_SETTINGS_PATH,
    exact: true,
  },
  // TODO: Remove! Keeping this solely for backwards compatibility
  {
    path: '/teams/:entityName',
    exact: true,
  },
  {
    path: REGISTRY_PATHS,
    exact: true,
  },
  {
    path: `${ARTIFACT_VERSION_PATH}/graph`,
    redirect: `${ARTIFACT_VERSION_PATH}/lineage`,
  },
  {
    path: '/models',
    redirect: '/registry/model',
  },
  {
    path: ENTITY_LAUNCH_PATHS,
    exact: true,
  },
  {
    path: AGENT_PATHS,
    exact: true,
  },
  {
    path: QUEUE_PATHS,
    exact: true,
  },
  {
    exact: true,
    path: PROJECT_LAUNCH_TAB_PATH,
    redirect: LAUNCH_PATH,
  },
  {
    path: GLOBAL_SANDBOXES_PATHS,
    exact: true,
  },
  {path: '/post-training', exact: true},
  {
    path: GROUP_PATHS,
    exact: true,
  },
  {
    path: CREATE_SWEEP_PATH,
    exact: true,
  },
  {
    path: SWEEP_PATHS,
    exact: true,
  },
  {
    path: ARIA_PATHS,
    exact: true,
  },
  {
    path: [MARIMO_NOTEBOOK_EDIT_PATH, MARIMO_NOTEBOOK_VIEW_PATH],
    exact: true,
  },
  {
    // This old edit report page route will be redirected to /reportlist
    path: OLD_REPORT_PAGE_EDIT_PATH,
    exact: true,
  },
  {
    // This old view report page route will be redirected to /reportlist
    path: OLD_REPORT_PAGE_VIEW_PATH,
    exact: true,
  },
  {
    path: REPORT_PAGE_EDIT_PATH,
    exact: true,
  },
  {
    path: REPORT_PAGE_VIEW_PATH,
    exact: true,
  },
  {
    path: REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
    exact: true,
  },
  {
    path: OLD_GALLERY_DISCUSSION_EDIT_PATH,
    exact: true,
  },
  {
    path: OLD_GALLERY_DISCUSSION_VIEW_PATH,
    exact: true,
  },
  {
    path: NO_ACCESS_PATH,
    exact: true,
  },
  {
    path: GLOBAL_HISTORIC_USAGE_PATH,
    exact: true,
  },
  {
    path: GLOBAL_HISTORIC_USAGE_REDIRECT_PATH,
    redirect: GLOBAL_HISTORIC_USAGE_REDIRECT_TO_PATH,
  },
  {
    path: HISTORIC_USAGE_PATH,
    exact: true,
  },
  {
    path: HISTORIC_USAGE_REDIRECT_PATH,
    redirect: HISTORIC_USAGE_REDIRECT_TO_PATH,
  },
  {
    path: PROJECT_PATHS,
    exact: true,
  },
  {
    path: REDIRECT_PATH,
    exact: true,
  },
  {
    // this is here to truncate extra path segments after the actual path
    path: PROJECT_BASE_PATH,
    redirect: PROJECT_BASE_PATH,
  },
  {
    path: TRIAL_END_PATH,
    exact: true,
  },
  {
    path: ACADEMIC_APPLICATION_PATH,
    exact: true,
  },
  {
    path: PROFILE_PATH,
    exact: true,
  },
];
